import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Session, User, AuthError } from '@supabase/supabase-js';
import { channelName, supabase } from './supabase';
import type { Profile } from '@/types/database';

export type OAuthProvider = 'google' | 'apple' | 'spotify';

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  isPremium: boolean;
  isAdmin: boolean;
  signInWithPassword: (email: string, password: string) => Promise<{
    error: AuthError | null;
  }>;
  signUpWithPassword: (
    email: string,
    password: string,
    displayName?: string,
    phone?: string,
    smsOptIn?: boolean,
  ) => Promise<{ error: AuthError | null; needsConfirmation: boolean }>;
  signInWithOAuth: (provider: OAuthProvider) => Promise<{
    error: AuthError | null;
  }>;
  sendPasswordReset: (email: string) => Promise<{ error: AuthError | null }>;
  updatePassword: (password: string) => Promise<{ error: AuthError | null }>;
  updateProfile: (
    patch: Partial<
      Pick<
        Profile,
        | 'display_name'
        | 'avatar_url'
        | 'bio'
        | 'preferred_language'
        | 'email_notifications_enabled'
      >
    >,
  ) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const siteUrl =
  typeof window !== 'undefined'
    ? window.location.origin
    : 'http://localhost:5173';

async function fetchProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .maybeSingle();
  if (error) {
    console.warn('[auth] failed to fetch profile', error.message);
    return null;
  }
  return data;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const profileChannelRef = useRef<ReturnType<
    typeof supabase.channel
  > | null>(null);

  // Initial session + subscribe to changes.
  useEffect(() => {
    let alive = true;

    void supabase.auth.getSession().then(({ data }) => {
      if (!alive) return;
      setSession(data.session);
      if (!data.session) setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
      if (!next) {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => {
      alive = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  // Keep profile in sync with session.
  useEffect(() => {
    const userId = session?.user.id;
    if (!userId) {
      if (profileChannelRef.current) {
        void supabase.removeChannel(profileChannelRef.current);
        profileChannelRef.current = null;
      }
      return;
    }

    let alive = true;
    setLoading(true);

    void fetchProfile(userId).then((p) => {
      if (!alive) return;
      setProfile(p);
      setLoading(false);
    });

    // Subscribe to realtime changes on our own profile row — covers
    // webhook-driven tier flips (subscriptions → profiles trigger).
    const channel = supabase
      .channel(channelName(`profile:${userId}`))
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${userId}`,
        },
        (payload) => {
          setProfile(payload.new as Profile);
        },
      )
      .subscribe();
    profileChannelRef.current = channel;

    return () => {
      alive = false;
      void supabase.removeChannel(channel);
      profileChannelRef.current = null;
    };
  }, [session?.user.id]);

  const refreshProfile = useCallback(async () => {
    if (!session?.user.id) return;
    const p = await fetchProfile(session.user.id);
    setProfile(p);
  }, [session?.user.id]);

  const signInWithPassword = useCallback<
    AuthContextValue['signInWithPassword']
  >(async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  }, []);

  const signUpWithPassword = useCallback<
    AuthContextValue['signUpWithPassword']
  >(async (email, password, displayName, phone, smsOptIn) => {
    const metadata: Record<string, unknown> = {};
    if (displayName) metadata.display_name = displayName;
    if (phone) metadata.phone = phone;
    if (smsOptIn) metadata.sms_opt_in = true;

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${siteUrl}/auth/callback`,
        data: metadata,
      },
    });
    const needsConfirmation = !error && !data.session;
    return { error, needsConfirmation };
  }, []);

  const signInWithOAuth = useCallback<AuthContextValue['signInWithOAuth']>(
    async (provider) => {
      const options: {
        redirectTo: string;
        scopes?: string;
      } = { redirectTo: `${siteUrl}/auth/callback` };
      if (provider === 'spotify') {
        options.scopes = 'user-read-email user-read-private';
      }
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options,
      });
      return { error };
    },
    [],
  );

  const sendPasswordReset = useCallback<
    AuthContextValue['sendPasswordReset']
  >(async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${siteUrl}/auth/reset`,
    });
    return { error };
  }, []);

  const updatePassword = useCallback<AuthContextValue['updatePassword']>(
    async (password) => {
      const { error } = await supabase.auth.updateUser({ password });
      return { error };
    },
    [],
  );

  const updateProfile = useCallback<AuthContextValue['updateProfile']>(
    async (patch) => {
      if (!session?.user.id) {
        return { error: new Error('Not signed in') };
      }
      const { error } = await supabase
        .from('profiles')
        .update(patch)
        .eq('id', session.user.id);
      if (error) return { error: new Error(error.message) };
      await refreshProfile();
      return { error: null };
    },
    [session?.user.id, refreshProfile],
  );

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setSession(null);
    setProfile(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      profile,
      loading,
      isPremium: profile?.membership_tier === 'superfan',
      isAdmin: profile?.is_admin === true,
      signInWithPassword,
      signUpWithPassword,
      signInWithOAuth,
      sendPasswordReset,
      updatePassword,
      updateProfile,
      signOut,
      refreshProfile,
    }),
    [
      session,
      profile,
      loading,
      signInWithPassword,
      signUpWithPassword,
      signInWithOAuth,
      sendPasswordReset,
      updatePassword,
      updateProfile,
      signOut,
      refreshProfile,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
